import { useApiStore } from '@/stores/api';
import { buildUrlWithParams } from '@/requests/url';
import type { AnyRatingService } from '@/stores/ratingService';
import { RatingServiceType, isBaseService } from '@/stores/ratingService';
import type { ServiceHash } from '@/stores/ImageMetadata';
import { useFetch } from '@/libraries/vue-fetch/composables/useFetch';

export async function login(): Promise<string> {
  const apiConfig = useApiStore();
  const url = apiConfig.host + '/session_key';

  const { requestId, fetch } = useFetch(url, {
    headers: {
      Accept: 'application/json',
      'Hydrus-Client-API-Access-Key': apiConfig.apiKey,
    },
  });
  const response = await fetch;
  const data = await response.json();
  return <string>data.session_key;
}
export async function getServices(): Promise<{
  [key: ServiceHash]: AnyRatingService;
}> {
  const apiConfig = useApiStore();
  if (apiConfig.sessionKey === null) {
    throw Error("sessionKey can't be null");
  }
  if (!apiConfig.voteServiceIds) {
    throw Error("voteServiceIds can't be null");
  }
  const returnData: { [key: ServiceHash]: AnyRatingService } = {};
  console.log('getServices (A): ', apiConfig.voteServiceIds);
  for (const serviceId of apiConfig.voteServiceIds) {
    console.log('getServices (B): ', serviceId);
    const url = buildUrlWithParams(
      apiConfig.host + '/get_service',
      { // eslint-disable-line prettier/prettier
        service_key: serviceId, // eslint-disable-line prettier/prettier
      }, // eslint-disable-line prettier/prettier
    );

    const { requestId, fetch } = useFetch(url, {
      headers: {
        Accept: 'application/json',
        'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
      },
    });
    const response = await fetch;
    returnData[serviceId] = (<any> await response.json()).service;
  }
  return returnData;
}

const RATING_TYPES = new Set<number>([RatingServiceType.Stars, RatingServiceType.Like, RatingServiceType.Counter]);

/**
 * Fetches all services (any type) from Hydrus via GET /get_services.
 * Returns a flat dict with service_key as keys and service objects as values.
 */
export async function getAllServicesRaw(): Promise<Record<string, any>> {
  const apiConfig = useApiStore();
  if (apiConfig.sessionKey === null) throw Error("sessionKey can't be null");

  const url = apiConfig.host + '/get_services';
  const { fetch } = useFetch(url, {
    headers: {
      Accept: 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
  });
  const response = await fetch;
  const data = await response.json();

  // /get_services returns { services: { [service_key]: { name, type, ... }, ... } }
  const collected: Record<string, any> = {};
  const raw: unknown = data.services;
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, val] of Object.entries(raw)) {
      if (val === null || typeof val !== 'object' || Array.isArray(val)) continue;
      const s = {
 service_key: key,
...(val as Record<string, unknown>),
};
      collected[key] = s;
    }
  }
  return collected;
}

/**
 * Fetches all rating services from Hydrus via GET /get_services.
 * Returns only services with type Stars (6), Like (7), or Counter (22).
 */
export async function getAllRatingServices(): Promise<AnyRatingService[]> {
  const servicesRaw = await getAllServicesRaw();
  const collected: AnyRatingService[] = [];
  for (const s of Object.values(servicesRaw)) {
    if (isBaseService(s) && RATING_TYPES.has(s.type)) {
      collected.push(s as AnyRatingService);
    }
  }
  return collected;
}

export type TagArray = Array<Array<string> | string>;

export function parseQuery(query: string): TagArray {
  const tags: TagArray = [];
  let tag: string = '';
  let escaped = false;
  for (let i = 0; i < query.length; i++) {
    const char = query[i];
    if (char === '\\') {
      escaped = !escaped;
      // eslint-disable-next-line prettier/prettier
    } else if (
        !escaped &&
        char === ','
    ) {
      tags.push(tag.trim());
      tag = '';
      // eslint-disable-next-line prettier/prettier
    } else if (
        !escaped &&
        char === '&' &&
        char.substring(i, i + 2) == '&&'
    ) {
      tags.push(tag.trim());
      tag = '';
      i += 1; // skip second &
    } else if (
      !escaped &&
      char === 'A' &&
      char.substring(i - 1, i + 4) == ' AND '
    ) {
      tags.push(tag.trim());
      tag = '';
      i += 1; // skip second &
      // eslint-disable-next-line prettier/prettier
    } else if (
      !escaped &&
      char === '_'
    ) {
      tag += ' ';
      // eslint-disable-next-line prettier/prettier
    } else if (
      !escaped &&
      char === '​'
    ) {
      // do nothing, ignore.
    } else {
      tag += char;
      if (escaped) {
        // turn of escape as we accepted the current character.
        // tags: ["system:limit = 2","penis_in_pussy"," spiked_collar"]
        escaped = false;
      }
    }
  }
  if (tag) {
    tags.push(tag.trim());
  }
  return tags;
}

export async function search(
  query: TagArray | string = ['system:limit=16'],
): Promise<number[]> {
  const apiConfig = useApiStore();
  if (typeof query === 'string') {
    query = parseQuery(query);
  }
  const url = buildUrlWithParams(apiConfig.host + '/get_files/search_files', {
    tags: JSON.stringify(query),
  });
  console.log(url);

  const { requestId, fetch } = useFetch(url, {
    headers: <HeadersInit>{
      Accept: 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
  });
  const response = await fetch;
  const data = await response.json();
  return <number[]>data.file_ids;
}

/** e.g. Hydrus 524 */
export type PageApiV44 = {
  name: string;
  page_key: string;
  page_type: number;
  focused: boolean;
  pages?: PageApiV44[];
};
export function isPageApiV44(obj: any): obj is PageApiV44 {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.name === 'string' &&
    isPageKey(obj.page_key) &&
    typeof obj.page_type === 'number' &&
    typeof obj.focused === 'boolean' &&
    (obj.pages === undefined || (Array.isArray(obj.pages) && obj.pages.every(isPageApiV44)))
  );
}

/** e.g. Hydrus 536 */
export type PageApiV49 = {
  name: string;
  page_key: string;
  page_state: number | PageState;
  page_type: number | PageType;
  selected: false;
  pages?: PageApiV49[];
};
export function isPageApiV49(obj: any): obj is PageApiV49 {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.name === 'string' &&
    isPageKey(obj.page_key) &&
    (typeof obj.page_state === 'number' && isPageState(obj.page_state)) &&
    (typeof obj.page_type === 'number' && isPageType(obj.page_type)) &&
    typeof obj.selected === 'boolean' &&
    (obj.pages === undefined || (Array.isArray(obj.pages) && obj.pages.every(isPageApiV49)))
  );
}

export function isPageKey(obj: any): obj is PageApiV44['page_key'] | PageApiV49['page_key'] {
  return typeof obj === 'string';
}

export type Page = PageApiV44 | PageApiV49;
export function isPage(obj: any): obj is Page {
  return isPageApiV44(obj) || isPageApiV49(obj);
}

type ActivePageReturn = {
  current: Page | null;
  all: Page;
};

export async function activePage(): Promise<ActivePageReturn> {
  const apiConfig = useApiStore();

  const url = buildUrlWithParams(
    apiConfig.host + '/manage_pages/get_pages',
    {},
  );
  console.log(url);

  const { requestId, fetch } = useFetch(url, {
    headers: <HeadersInit>{
      Accept: 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
  });
  const response = await fetch;
  const data = await response.json();
  let selectedPageIterator: Page = data.pages;
  while (selectedPageIterator && selectedPageIterator.pages) {
    selectedPageIterator = selectedPageIterator.pages.filter(
      // @ts-ignore
      (page: Page) => page.focused ?? page.selected,
    )[0];
  }
  return {
 current: selectedPageIterator || null,
all: data.pages,
};
}

/**
 * page_state is as follows:
 *
 * 0 - ready
 * 1 - initialising
 * 2 - searching/loading
 * 3 - search cancelled
 */
export enum PageState {
  READY = 0,
  INITIALISING = 1,
  LOADING = 2,
  CANCELLED = 3,
}
function isPageState(value: any): value is PageState {
  return Object.values(PageState).includes(value);
}

/**
 * page_type is as follows:
 *
 * 1 - Gallery downloader
 * 2 - Simple downloader
 * 3 - Hard drive import
 * 5 - Petitions (used by repository janitors)
 * 6 - File search
 * 7 - URL downloader
 * 8 - Duplicates
 * 9 - Thread watcher
 * 10 - Page of pages
 */
export enum PageType {
  GALLERY_DOWNLOADER = 1,
  SIMPLE_DOWNLOADER = 2,
  HARD_DRIVE_IMPORT = 3,
  PETITIONS = 5,
  FILE_SEARCH = 6,
  URL_DOWNLOADER = 7,
  DUPLICATES = 8,
  THREAD_WATCHER = 9,
  PAGE_OF_PAGES = 10,
}
function isPageType(value: any): value is PageType {
  return Object.values(PageType).includes(value);
}

export type PageDetail = {
  name: string;
  page_key: string;
  page_type: number;
  page_state: PageState;
  management: any;
  media: { num_files: number; hash_ids: number[] };
};

export async function pageInfo(pageKey: string): Promise<PageDetail> {
  const apiConfig = useApiStore();

  const url = buildUrlWithParams(
    apiConfig.host + '/manage_pages/get_page_info',
    {
      page_key: pageKey,
    },
  );
  console.log({
 url,
pageKey,
});

  const { requestId, fetch } = useFetch(url, {
    headers: <HeadersInit>{
      Accept: 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
  });
  const response = await fetch;
  const data = await response.json();
  return data.page_info;
}

/** forces the server to reload/research the current page. It's like hitting F5 in the client **/
export async function refreshPage(pageKey: string): Promise<void> {
  const apiConfig = useApiStore();

  const url = apiConfig.host + '/manage_pages/refresh_page';
  console.log(url);

  const { requestId, fetch } = useFetch(url, {
    method: 'POST',
    headers: <HeadersInit>{
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Hydrus-Client-API-Session-Key': apiConfig.sessionKey,
    },
    body: JSON.stringify({
      page_key: pageKey,
    }),
  });
  const response = await fetch;
  if (response.status !== 200) {
    console.error('refreshPage error:', response);
    throw Error(`refreshPage error for key ${pageKey}.`);
  }
}
